import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import NotificationSettingsBox from 'src/components/NotificationSettingsBox/NotificationSettingsBox'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { NotificationSettingsResponse } from 'src/queries/notificationSettings/notificationSettings.models'
import {
  mergePatchedNotificationSettings,
  notificationSettingsMutations,
  notificationSettingsQueries,
  notificationSettingsQueryKeys,
  toNotificationSettingListModels,
} from 'src/queries/notificationSettings/notificationSettings.queries'
import { selectFeatureFlaggerCookiesFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectAppName,
  selectEnvConfigFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ProfileNotifications: React.FC = () => {
  const { data: notificationSettings = [] } = useQuery({
    ...notificationSettingsQueries.list(),
    select: toNotificationSettingListModels,
  })
  const appName = useSelector(selectAppName)

  const translate = useTranslate()

  const envConfig = useSelector(selectEnvConfigFromState)
  const user = useCurrentUser()

  const showSMSSettings = useSelector(
    selectFeatureFlaggerCookiesFromState,
  )?.show_sms_settings

  const queryClient = useQueryClient()
  const patchMutation = useMutation({
    ...notificationSettingsMutations.patch(),
    onSuccess: (data) => {
      // Parity with the legacy reducer merge: patched settings replace their
      // cached versions in place the instant the PATCH resolves...
      queryClient.setQueryData<NotificationSettingsResponse>(
        notificationSettingsQueryKeys.list(),
        (existing) => mergePatchedNotificationSettings(existing, data),
      )
      // ...then a background refetch restores the canonical server order.
      queryClient.invalidateQueries({
        queryKey: notificationSettingsQueryKeys.all(),
      })
    },
  })

  return (
    <>
      <Helmet>
        <title>{`Notification Settings | ${appName}`}</title>
        <meta
          name="description"
          content={`Notification Settings page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent>
        <NotificationSettingsBox
          notificationSettings={notificationSettings}
          patchNotifications={(body) => patchMutation.mutate(body)}
        />
        {showSMSSettings && (
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              max-width: 440px;
            `}
          >
            <span
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                ${getFontCss(
                  20,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('SMS Notifications')}
            </span>
            <span
              css={css`
                margin-top: 30px;
              `}
            >
              {user?.smsEnabled
                ? translate(
                    'You are getting alerts via SMS. To stop receiving SMS alerts text STOP to ${phoneNumber}',
                    { phoneNumber: envConfig.TWILIO_FROM_PHONE_NUMBER || '' },
                  )
                : translate(
                    'You have opted out. Opt in by texting START to ${phoneNumber}',
                    { phoneNumber: envConfig.TWILIO_FROM_PHONE_NUMBER || '' },
                  )}
            </span>
          </Box>
        )}
      </BackgroundPageContent>
    </>
  )
}

export default ProfileNotifications
